import { useQuery } from "@tanstack/react-query";

export const CROSSD_APR_QUERY_KEY = "crossd-apr";
const CROSSD_APR_STALE_TIME_MS = 60_000;

/**
 * CROSSD 대시보드 overview API. prd 환경만 존재하므로 URL을 고정한다.
 * 응답의 `est_last_epoch_apy`는 비율값(1 == 100%, 0.05 == 5%).
 */
export const CROSSD_OVERVIEW_URL =
  "https://crossd-dashboard-api.crossdefi.io/api/v1/overview";

interface CrossdOverviewResponse {
  code: number;
  message: string;
  data: {
    est_last_epoch_apy: string;
  };
}

let cachedApr: { value: number; expiresAt: number } | undefined;
let aprRequest: Promise<number> | undefined;

export function __clearCrossdAprCacheForTest(): void {
  cachedApr = undefined;
  aprRequest = undefined;
}

/** Shared by WalletInfo and GetOneUsd so colocated widgets issue one request. */
export function fetchCrossdAprPercent(): Promise<number> {
  if (cachedApr && cachedApr.expiresAt > Date.now()) {
    return Promise.resolve(cachedApr.value);
  }
  if (aprRequest) return aprRequest;

  aprRequest = (async () => {
    const res = await fetch(CROSSD_OVERVIEW_URL, { cache: "no-store" });
    if (!res.ok) {
      throw new Error(`Failed to fetch CROSSD overview: ${res.status}`);
    }
    const json: CrossdOverviewResponse = await res.json();
    if (json.code !== 200) {
      throw new Error(`CROSSD overview API error: ${json.code}`);
    }
    const ratio = parseFloat(json.data.est_last_epoch_apy);
    if (!Number.isFinite(ratio)) {
      throw new Error("CROSSD overview returned a non-numeric APR");
    }
    const value = ratio * 100;
    cachedApr = { value, expiresAt: Date.now() + CROSSD_APR_STALE_TIME_MS };
    return value;
  })().finally(() => {
    aprRequest = undefined;
  });

  return aprRequest;
}

export function formatCrossdAprPercent(aprPercent: number): string {
  const magnitude = Math.abs(aprPercent);
  const rounded =
    (aprPercent >= 0 ? Math.floor : Math.ceil)(magnitude * 100) / 100;
  return `APR ${aprPercent >= 0 ? "+" : "-"}${rounded}%`;
}

/**
 * CROSSD APR(`est_last_epoch_apy`)를 백분율 숫자로 반환한다.
 * API가 prd 전용이라 env와 무관하게 항상 prd URL에서 fetch해 표기한다.
 */
export function useCrossdApr() {
  const { data, ...rest } = useQuery<number>({
    queryKey: [CROSSD_APR_QUERY_KEY],
    queryFn: fetchCrossdAprPercent,
    staleTime: CROSSD_APR_STALE_TIME_MS,
  });

  return { aprPercent: data ?? null, ...rest };
}
